import { getLocaleNumericFormat } from '@philips-internal/oswald-ui-model/lib/generators/numeric/numeric-utils.js';

/**
 * Checks if a value is a valid decimal number (not an integer)
 * @param value - Value to be checked
 * @returns true if it's a valid decimal number
 */
function isDecimalNumber(value: unknown): boolean {
  if (value === undefined || value === null || value === '') {
    return false;
  }

  const valueNumber = Number(value);
  return !isNaN(valueNumber) && !Number.isInteger(valueNumber);
}

/**
 * Formats a decimal numeric value using the appropriate locale decimal separator
 * @param value - Value to be formatted (string, number, or unknown)
 * @param decimalSeparator - Optional decimal separator. If not provided, will be obtained from locale
 * @returns Formatted string with the correct decimal separator, or the original value if not a decimal number
 * @example
 * // For pt-BR locale (separator = ',')
 * formatDecimalValue(3.14) // '3,14'
 * formatDecimalValue('5.5') // '5,5'
 * formatDecimalValue(10) // '10'
 * formatDecimalValue(3.14, ',') // '3,14' (with explicit separator)
 */
function formatDecimalValue(value: unknown, decimalSeparator?: string): string {
  if (value === undefined || value === null || value === '') {
    return String(value);
  }

  const DECIMAL_SEPARATOR = decimalSeparator ?? getLocaleNumericFormat().DECIMAL_SEPARATOR;
  const stringValue = String(value);

  if (stringValue.includes(DECIMAL_SEPARATOR) && !stringValue.includes('.')) {
    return stringValue;
  }

  if (stringValue.includes('.') && isDecimalNumber(value)) {
    const parts = stringValue.split('.');

    if (parts.length === 2 && parts[1].length > 0) {
      return `${parts[0]}${DECIMAL_SEPARATOR}${parts[1]}`;
    }
  }

  return stringValue;
}

/**
 * Formats multiple decimal values in an object (mutates the object in place)
 * @param values - Object containing values to be formatted (will be modified)
 * @example
 * const data = { temp: 36.5, weight: 70.25, age: 30 };
 * formatDecimalValues(data); // data is now { temp: '36,5', weight: '70,25', age: 30 }
 */
function formatDecimalValues<T extends Record<string, unknown>>(values: T): void {
  const decimalSeparator = getLocaleNumericFormat().DECIMAL_SEPARATOR;

  for (const field in values) {
    if (Object.hasOwn(values, field)) {
      const value = values[field];
      const formattedValue = formatDecimalValue(value, decimalSeparator);

      if (formattedValue !== String(value)) {
        values[field] = formattedValue as T[Extract<keyof T, string>];
      }
    }
  }
}

const NumericFormatterUtil = {
  formatDecimalValue,
  formatDecimalValues,
};

export default NumericFormatterUtil;
